import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';
import type {
  ActivityType,
  Deal,
  DealActivity,
  DealPriority,
} from './comercial-types';

/**
 * Store do Comercial — **backend-first (Rodada R3.5, 2026-05-18)**.
 *
 * Substitui o mock `comercial-mock.ts` (deletado) por consumo do endpoint
 * `/api/v1/deals` via React Query. Contratos públicos preservados:
 *
 *   - useDeals(): Deal[]           → useQuery list
 *   - useDeal(id): Deal|undefined  → useQuery single (com activities)
 *   - useDealActions():            → mutations REST
 *
 * Componentes consumidores (`ComercialKanban`, `ComercialList`,
 * `DealDetailDialog`, `DealFormDialog`, `ComercialPage`) seguem usando
 * `useDeals` / `useDealActions` sem modificação além do tratamento de
 * estado async (loading/erro).
 *
 * Activities: o LIST não retorna activities (evita N+1). `useDeal(id)`
 * SEMpre retorna deal completo com activities. DealDetailDialog refetcha
 * o deal completo quando abre.
 *
 * Polling: `refetchInterval: 30_000` na list pra cross-device sync (consistente
 * com pattern das outras stores backend-first).
 */

// ─── Tipos do backend ──────────────────────────────────────────────────

interface BackendDeal {
  id: string;
  workflowSlug: string;
  cliente: string;
  fase: string;
  responsavelId: string | null;
  valor: number;
  prioridade: DealPriority;
  fields: BackendDealFields;
  criadoPor: string;
  criadoEmIso: string;
  atualizadoEmIso: string;
  arquivado: boolean;
  arquivadoEmIso: string | null;
  // FEAT-V2-034 / BUG-V2-045: backend pré-resolve numeração humana.
  numeroHumano?: number | null;
  humanLabel?: string;
  // FEAT-V2-035 / BUG-V2-047: soft delete (true=ativo, default).
  ativo?: boolean;
  // Projetos 2.0 Parte 2 / P1: tipo da venda + FK cliente V2 (cliente_id).
  tipoVenda?: 'projeto' | 'recorrente' | null;
  clienteV2Id?: string | null;
  // BUG-V2-058: backend retorna tags pra render no header do DealCard.
  // Shape mínimo bate com `Tag` (tags-store.ts) — TagBadge consome.
  tags?: Array<{
    id: string;
    nome: string;
    cor: string;
    icone: string | null;
    ativo: boolean;
  }>;
}

interface BackendDealFields {
  titulo?: string;
  produto?: string;
  servico?: string;
  responsavelNome?: string;
  proximaAcao?: string;
  proximaAcaoDataIso?: string;
  observacoes?: string;
  origem?: string;
  [k: string]: unknown;
}

interface BackendActivity {
  id: string;
  dealId: string;
  tipo: string;
  texto: string | null;
  metadata: Record<string, unknown>;
  autorId: string;
  criadoEmIso: string;
}

interface BackendDealWithActivities extends BackendDeal {
  activities: BackendActivity[];
}

interface BackendStageResponse {
  deal: BackendDeal;
  runs: { left: unknown[]; entered: unknown[] };
}

// ─── Adapter backend → frontend Deal ───────────────────────────────────

const TIPO_TO_TYPE: Record<string, ActivityType> = {
  criacao: 'criacao',
  stage_change: 'fase_alterada', // legacy frontend type name
  ligacao: 'ligacao',
  email: 'email',
  reuniao: 'reuniao',
  proposta: 'proposta',
  nota: 'nota',
  comentario: 'nota', // fallback até frontend ter type comentario
  automation: 'automation',
};

function mapActivity(a: BackendActivity): DealActivity {
  const meta = (a.metadata ?? {}) as { description?: unknown; autorName?: unknown };
  return {
    id: a.id,
    dealId: a.dealId,
    type: TIPO_TO_TYPE[a.tipo] ?? 'nota',
    title: a.texto ?? '',
    description: typeof meta.description === 'string' ? meta.description : undefined,
    userId: a.autorId,
    userName: typeof meta.autorName === 'string' ? meta.autorName : a.autorId,
    createdAtIso: a.criadoEmIso,
  };
}

/**
 * Backend → Frontend: divide valor por 100 (backend armazena CENTAVOS,
 * frontend exibe REAIS com decimais — R$ 1.249,90).
 * Decisão de 2026-05-18 (hot fix pré-merge PR #2). Ver
 * server/db/migrations/005-deals-and-activities.sql.
 */
function mapDeal(b: BackendDeal, activities: BackendActivity[] = []): Deal {
  const f = b.fields ?? {};
  return {
    id: b.id,
    // BUG-V2-045: propaga numeração humana pra UI consumir via `humanLabel ?? id`.
    numeroHumano: b.numeroHumano ?? null,
    humanLabel: b.humanLabel ?? b.id,
    titulo: f.titulo ?? '',
    // Backend column ainda se chama `cliente` (sem migration SQL — BUG-V2-001
    // 2026-05-18). Frontend chama de `clienteId` pra deixar a semântica de FK
    // explícita. `useClientNome(clienteId)` faz lookup pra renderizar.
    clienteId: b.cliente,
    produto: f.produto ?? '',
    servico: f.servico ?? '',
    valor: b.valor / 100,
    fase: b.fase,
    prioridade: b.prioridade,
    responsavelId: b.responsavelId ?? '',
    responsavelNome: f.responsavelNome ?? '',
    proximaAcao: f.proximaAcao,
    proximaAcaoDataIso: f.proximaAcaoDataIso,
    criadoEmIso: b.criadoEmIso,
    atualizadoEmIso: b.atualizadoEmIso,
    observacoes: f.observacoes,
    origem: f.origem,
    // Projetos 2.0 Parte 2 / P1: tipo da venda + cliente V2 (herdados pela automação).
    tipoVenda: b.tipoVenda ?? null,
    clienteV2Id: b.clienteV2Id ?? null,
    // BUG-B1.6.2: descricao/foto pra deals do workflow corretiva (Portal Cliente).
    descricao: (f as Record<string, unknown>).descricao as string | undefined,
    fotoR2Key: ((f as Record<string, unknown>).foto_r2_key as string | null | undefined) ?? null,
    // BUG-V2-047: ativo default true (compat pré-migration 051).
    ativo: b.ativo ?? true,
    // BUG-V2-058: tags pré-carregadas pra render no header do DealCard.
    tags: b.tags ?? [],
    atividades: activities.map(mapActivity),
  };
}

/**
 * Reais (frontend) → Centavos (backend). `Math.round` evita imprecisão
 * de float (1249.9 × 100 = 124990.00000000001 em IEEE 754).
 */
function reaisToCentavos(reais: number): number {
  return Math.round(reais * 100);
}

// ─── Query keys ────────────────────────────────────────────────────────

const KEYS = {
  // BUG-V2-059: includeInactive vai no queryKey pra trocar fetch quando
  // toggle "Mostrar inativos" muda — TanStack refetch automático.
  list: (workflow: string, includeInactive = false) =>
    ['v1', 'deals', 'list', workflow, { includeInactive }] as const,
  single: (id: string) => ['v1', 'deals', 'single', id] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────

interface UseDealsOptions {
  workflow?: string;
  /**
   * BUG-V2-059: quando true, envia `?include_inactive=1` pro backend.
   * Sem isso, backend filtra `ativo=1` por default e cards arquivados nem
   * chegam ao frontend (filtro frontend `mostrarInativos` é no-op).
   */
  includeInactive?: boolean;
}

function buildDealsUrl(workflow: string, includeInactive: boolean): string {
  const params = new URLSearchParams({ workflow });
  if (includeInactive) params.set('include_inactive', '1');
  return `/api/v1/deals?${params.toString()}`;
}

export function useDeals(
  workflowOrOpts: string | UseDealsOptions = 'comercial',
): Deal[] {
  const opts: UseDealsOptions =
    typeof workflowOrOpts === 'string' ? { workflow: workflowOrOpts } : workflowOrOpts;
  const workflow = opts.workflow ?? 'comercial';
  const includeInactive = opts.includeInactive ?? false;
  const { data } = useQuery({
    queryKey: KEYS.list(workflow, includeInactive),
    queryFn: async () => {
      const rows = await api.get<BackendDeal[]>(buildDealsUrl(workflow, includeInactive));
      return rows.map((r) => mapDeal(r));
    },
    enabled: hasToken(),
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  return data ?? [];
}

/**
 * Hook com estados completos (data + isLoading + isError + error + refetch).
 *
 * Usado pelo ComercialPage pra distinguir "ainda carregando" de "carregado
 * mas vazio" — sem isso, a UI mostrava EmptyState prematuro durante o boot
 * (parecia que não tinha card cadastrado quando era só fetch em flight).
 *
 * Frente A R1 (2026-05-25): substitui a interpretação ambígua de `useDeals()
 * → []` no boot. `useDealsQuery` deixa o caller decidir entre skeleton e
 * empty state.
 */
export function useDealsQuery(workflowOrOpts: string | UseDealsOptions = 'comercial') {
  const opts: UseDealsOptions =
    typeof workflowOrOpts === 'string' ? { workflow: workflowOrOpts } : workflowOrOpts;
  const workflow = opts.workflow ?? 'comercial';
  const includeInactive = opts.includeInactive ?? false;
  return useQuery({
    queryKey: KEYS.list(workflow, includeInactive),
    queryFn: async () => {
      const rows = await api.get<BackendDeal[]>(buildDealsUrl(workflow, includeInactive));
      return rows.map((r) => mapDeal(r));
    },
    enabled: hasToken(),
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

export function useDeal(id: string | null | undefined): Deal | undefined {
  const { data } = useQuery({
    queryKey: KEYS.single(id ?? '__none__'),
    queryFn: async () => {
      const r = await api.get<BackendDealWithActivities>(`/api/v1/deals/${id}`);
      return mapDeal(r, r.activities ?? []);
    },
    enabled: hasToken() && !!id,
    staleTime: 30_000,
  });
  return data;
}

/**
 * Hook completo com `isError` — usado pelo deeplink do ComercialPage pra
 * distinguir "ainda carregando" de "404 / não existe". Sem isso, deeplink
 * com ID inválido fica em loading silencioso indefinido.
 */
export function useDealQuery(id: string | null | undefined) {
  return useQuery({
    queryKey: KEYS.single(id ?? '__none__'),
    queryFn: async () => {
      const r = await api.get<BackendDealWithActivities>(`/api/v1/deals/${id}`);
      return mapDeal(r, r.activities ?? []);
    },
    enabled: hasToken() && !!id,
    staleTime: 30_000,
  });
}

// ─── Actions ───────────────────────────────────────────────────────────

/** Payload de criação — preserva interface do mock antigo. */
export interface CreateDealInput {
  titulo: string;
  /** FK pra clients.id (formato `client-<uuid>`). Ver `Deal.clienteId`. */
  clienteId: string;
  produto: string;
  servico: string;
  valor: number;
  prioridade: DealPriority;
  responsavelId: string;
  responsavelNome: string;
  proximaAcao?: string;
  proximaAcaoDataIso?: string;
  observacoes?: string;
  origem?: string;
  fase?: string;
  // Projetos 2.0 Parte 2 / P1: só relevantes com a flag PROJETOS_V2_ENABLED ON.
  tipoVenda?: 'projeto' | 'recorrente' | null;
  clienteV2Id?: string | null;
}

interface BackendCreatePayload {
  workflowSlug: string;
  cliente: string;
  fase: string;
  responsavelId: string | null;
  valor: number;
  prioridade: DealPriority;
  tipoVenda?: 'projeto' | 'recorrente' | null;
  clienteV2Id?: string | null;
  fields: BackendDealFields;
}

function inputToBackendPayload(input: CreateDealInput, workflow: string): BackendCreatePayload {
  return {
    workflowSlug: workflow,
    // SQL column ainda se chama `cliente` (sem migration por enquanto —
    // BUG-V2-001). Adapter faz a tradução do nome do campo do frontend
    // (`clienteId`) pro nome da coluna do backend (`cliente`).
    cliente: input.clienteId,
    fase: input.fase ?? 'lead',
    responsavelId: input.responsavelId || null,
    valor: reaisToCentavos(input.valor),
    prioridade: input.prioridade,
    // P1: só enviados quando a UI (flag ON) os preenche; senão undefined → backend default null.
    tipoVenda: input.tipoVenda,
    clienteV2Id: input.clienteV2Id,
    fields: {
      titulo: input.titulo,
      produto: input.produto,
      servico: input.servico,
      responsavelNome: input.responsavelNome,
      proximaAcao: input.proximaAcao,
      proximaAcaoDataIso: input.proximaAcaoDataIso,
      observacoes: input.observacoes,
      origem: input.origem,
    },
  };
}

export function useDealActions(workflow = 'comercial') {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidateList = () => qc.invalidateQueries({ queryKey: KEYS.list(workflow) });
  const invalidateSingle = (id: string) =>
    qc.invalidateQueries({ queryKey: KEYS.single(id) });

  const createDeal = useCallback(
    async (input: CreateDealInput): Promise<Deal> => {
      setPending('create');
      try {
        const r = await api.post<BackendDeal>(
          '/api/v1/deals',
          inputToBackendPayload(input, workflow)
        );
        const deal = mapDeal(r);
        await invalidateList();
        return deal;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, workflow]
  );

  const updateDeal = useCallback(
    async (id: string, patch: Partial<Deal>): Promise<void> => {
      setPending(id);
      try {
        // Backend aceita {cliente, responsavelId, valor, prioridade, fields:{...}}
        const fields: BackendDealFields = {};
        if (patch.titulo !== undefined) fields.titulo = patch.titulo;
        if (patch.produto !== undefined) fields.produto = patch.produto;
        if (patch.servico !== undefined) fields.servico = patch.servico;
        if (patch.responsavelNome !== undefined) fields.responsavelNome = patch.responsavelNome;
        if (patch.proximaAcao !== undefined) fields.proximaAcao = patch.proximaAcao;
        if (patch.proximaAcaoDataIso !== undefined)
          fields.proximaAcaoDataIso = patch.proximaAcaoDataIso;
        if (patch.observacoes !== undefined) fields.observacoes = patch.observacoes;
        if (patch.origem !== undefined) fields.origem = patch.origem;

        const body: Record<string, unknown> = {};
        // SQL column `cliente` ↔ TS field `clienteId` (BUG-V2-001).
        if (patch.clienteId !== undefined) body.cliente = patch.clienteId;
        if (patch.responsavelId !== undefined) body.responsavelId = patch.responsavelId || null;
        // valor: reais (frontend) → centavos (backend) via Math.round
        if (patch.valor !== undefined) body.valor = reaisToCentavos(patch.valor);
        if (patch.prioridade !== undefined) body.prioridade = patch.prioridade;
        // P1: tipo da venda + cliente V2 (só preenchidos pela UI com flag ON).
        if (patch.tipoVenda !== undefined) body.tipoVenda = patch.tipoVenda;
        if (patch.clienteV2Id !== undefined) body.clienteV2Id = patch.clienteV2Id;
        if (Object.keys(fields).length > 0) body.fields = fields;

        await api.put<BackendDeal>(`/api/v1/deals/${id}`, body);
        await invalidateList();
        await invalidateSingle(id);
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, workflow]
  );

  const deleteDeal = useCallback(
    async (id: string): Promise<void> => {
      setPending(id);
      try {
        await api.delete(`/api/v1/deals/${id}`);
        await invalidateList();
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, workflow]
  );

  /**
   * Mudar fase — backend dispara automações internamente (commit 3 da R3.5).
   *
   * `customLabels` opcional: caller passa labels resolvidos via `useColumnLabels`
   * (admin pode ter renomeado colunas). Se omitido, backend resolve via
   * `resolve-stage-label.ts` (override → custom column → slug cru).
   *
   * `userId`/`userName` mantidos por compat com signature antiga, mas backend
   * usa `req.user` da auth pra atribuir autor da atividade — params são
   * ignorados (mantém pra não quebrar callers).
   */
  const changeStage = useCallback(
    async (
      id: string,
      novaFase: string,
      _userId: string,
      _userName: string,
      customLabels?: { from?: string; to?: string }
    ): Promise<void> => {
      void _userId;
      void _userName;
      setPending(id);
      try {
        await api.put<BackendStageResponse>(`/api/v1/deals/${id}/stage`, {
          fase: novaFase,
          fromLabel: customLabels?.from,
          toLabel: customLabels?.to,
        });
        await invalidateList();
        await invalidateSingle(id);
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, workflow]
  );

  const addActivity = useCallback(
    async (
      dealId: string,
      activity: Omit<DealActivity, 'id' | 'dealId' | 'createdAtIso'>
    ): Promise<DealActivity> => {
      setPending(dealId);
      try {
        // Mapeia type frontend → tipo backend
        const tipo = activity.type === 'fase_alterada' ? 'stage_change' : activity.type;
        const r = await api.post<BackendActivity>(`/api/v1/deals/${dealId}/activities`, {
          tipo,
          texto: activity.title,
          metadata: {
            description: activity.description,
            autorName: activity.userName,
          },
        });
        await invalidateSingle(dealId);
        await invalidateList();
        return mapActivity(r);
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, workflow]
  );

  return { createDeal, updateDeal, deleteDeal, changeStage, addActivity, pending };
}

// ─── Busca de origem (Projetos 2.0 F1.1) ────────────────────────────────
//
// Seletor leve usado pelo wizard de Novo Projeto pra vincular o projeto a uma
// venda do Comercial. Consome GET /api/v1/deals/buscar (shape enxuto, distinto
// do Deal completo). NÃO substitui useDeals — é só pro seletor de origem.

export interface DealOrigemResult {
  id: string;
  numeroHumano: number | null;
  /** Rótulo humano pronto (`#1234` ou o id). */
  humanLabel: string;
  /** Nome do cliente já resolvido (pode ser texto legado). */
  cliente: string;
  /** FK pra `clientes` V2 — pré-preenche o cliente no wizard. NULL = manual. */
  clienteV2Id: string | null;
  /** Valor da venda em CENTAVOS. Converter pra reais (÷100) ao usar. */
  valorCentavos: number;
  fase: string;
}

export function useBuscarDealsOrigem(q: string, enabled = true) {
  return useQuery({
    queryKey: ['deals', 'buscar-origem', q],
    queryFn: () =>
      api.get<{ items: DealOrigemResult[] }>(
        `/api/v1/deals/buscar?workflow=comercial${q ? `&q=${encodeURIComponent(q)}` : ''}`,
      ),
    enabled: hasToken() && enabled,
    staleTime: 10_000,
  });
}
